/**
 * ScheduleReportDrawer — create-mode drawer for scheduling a report.
 *
 * Captures: description, frequency, day selection, send time, attachments, recipients.
 * Filters and time range are read-only, inherited from the parent page state.
 *
 * Designed to be reusable across all report pages by passing reportTypeId. The form
 * cards and their state are shared with the edit drawer (scheduled/components/
 * ScheduleFormCards.jsx + useScheduleForm.js).
 *
 * Props:
 *   open, onClose, onSave, saving,
 *   reportTypeId, rangeId, rangeName,
 *   reportFilters, filterOptions, campusId, frequencies, tzOffset,
 *   isCloud — the campus is 5.0 cloud (Maintenance shows its cloud filter groups)
 */

import { useEffect, useCallback, useMemo } from 'react';
import { calculateSendTime } from '../../../../libs.jsx';
import { getSession } from '../../../../services/session.js';
import { stateToFilterTerms } from '../../../../utils/reportFilterTerms.js';
import { buildFilterChips } from '../../../../utils/reportFilterChips.js';
import { describeTimeWindow } from '../../../../utils/reportTimeWindow.js';
import { offersCsvDetails, reportFilterSectionsFor, scheduleSupportsTimeWindow } from '../../../../utils/reportTypes.js';
import { useScheduleForm, useRecipientGate, useSubmitGuard, useDrawerFocus } from '../../scheduled/components/useScheduleForm.js';
import {
    ScheduleDetailsCard, ScheduleAttachmentsCard, AppliedFilters, ScheduleRecipientsCard,
} from '../../scheduled/components/ScheduleFormCards.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

const ScheduleReportDrawer = ({
    open, onClose, onSave, saving,
    reportTypeId, rangeId, rangeName,
    reportFilters, filterOptions, campusId, frequencies, tzOffset, isCloud = false,
}) => {
    const form = useScheduleForm();
    const { load } = form;
    const { busy, submit } = useSubmitGuard(saving);

    /* User Recipients are role-gated, as in the edit drawer: you may only add users you
       outrank. filterOptions.users has no role weights, so the gate loads them on open. */
    const myRoleWeight = useMemo(() => getSession()?.role_weight || 10, []);
    const gate = useRecipientGate({ open, campusId, myRoleWeight, fetchWeights: true });
    const userOptions = useMemo(() => filterOptions?.users || [], [filterOptions]);

    /* Read-only chips, the same as the report page shows (names, one per filter). */
    const filterChips = useMemo(
        () => buildFilterChips(reportFilters, filterOptions, reportFilterSectionsFor(reportTypeId, { isCloud })),
        [reportFilters, filterOptions, reportTypeId, isCloud],
    );
    /* The time-of-day window is saved with the schedule (time_window), so say so here
       too, in the page header's own "Yesterday · 8:00 AM – Midnight" form. */
    const windowLabel = scheduleSupportsTimeWindow(reportTypeId)
        ? describeTimeWindow(reportFilters?.timeWindow?.start, reportFilters?.timeWindow?.end)
        : '';

    // Reset form when drawer opens
    useEffect(() => { if (open) load(); }, [open, load]);
    useDrawerFocus(open, 'srd-description');

    const handleSubmit = useCallback(() => {
        if (busy) return;
        const nextSendTime = calculateSendTime(
            form.frequencyId, form.freqRate, form.sendTime, parseInt(tzOffset || 0, 10),
        );
        /* The stored filter JSON (utils/reportFilterTerms.js) — the create path is the
           one that writes time_window (all day as [0, 1440] with an empty label). */
        const filterTerms = stateToFilterTerms(reportFilters, filterOptions, { includeTimeWindow: true });
        const payload = {
            ...form.payloadFields(),
            campus_id: campusId,
            enabled: 1,
            report_type_id: reportTypeId,
            report_interval_id: rangeId,
            report_filter_terms: filterTerms,
            /* Second layer: drop anyone this user does not outrank, even if they got
               into the Set somehow. */
            report_user_ids: [...form.selectedUserIds]
                .filter((id) => gate.canManageUser(userOptions.find((u) => String(u.user_id) === id) || { user_id: id }))
                .join(','),
            next_send_time: nextSendTime,
        };
        submit(() => onSave(payload));
    }, [busy, form, tzOffset, reportFilters, filterOptions, campusId, reportTypeId, rangeId, gate, userOptions, submit, onSave]);

    const canSubmit = form.description.trim() && (form.emails.length > 0 || form.selectedUserIds.size > 0);

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Schedule Report"
                size="lg"
                ariaLabel="Schedule Report"
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={busy} type="button">Cancel</button>
                        <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={busy || !canSubmit} type="button">
                            {busy ? 'Creating…' : 'Create Schedule'}
                        </button>
                    </>
                )}
            >
                <ScheduleDetailsCard
                    form={form}
                    frequencies={frequencies}
                    idPrefix="srd"
                    after={(
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Report Range</span>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                <i className="fe fe-clock" style={{ marginRight: '4px' }} />
                                {windowLabel ? `${rangeName} · ${windowLabel}` : rangeName}
                            </span>
                        </div>
                    )}
                />

                <ScheduleAttachmentsCard form={form} showCsv={offersCsvDetails(reportTypeId)}>
                    <AppliedFilters chips={filterChips} sublabel="Filters are set via Report Settings" />
                </ScheduleAttachmentsCard>

                <ScheduleRecipientsCard form={form} users={userOptions} gate={gate} />
            </Drawer>
        </>
    );
};

export default ScheduleReportDrawer;
